"use client"

import React, { useState, useMemo } from "react"
import { Bell, MessageCircle } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu"
import { Button } from "./ui/button"
import { Badge } from "./ui/badge"
import { ScrollArea } from "./ui/scroll-area"
import { useSession } from "../context/SessionContext"
import { useTranslation } from "react-i18next"
import AssignmentChatDialog from "./AssignmentChatDialog"

interface AssignedNotification {
  id: number
  date: string
  question: string
  is_urgent: boolean
  is_read: boolean
  user_id: string
  assigned_by: string
  assigned_to: string
  collaborations: any[]
  is_open: boolean
  is_important: boolean
  is_ignored: boolean
}

const NotificationBell: React.FC = () => {
  const { t } = useTranslation()
  const { 
    session, 
    allAssistants, 
    myUsers, 
    notifications,
    setActivePhone,
    setActiveNotification,
    setViewingNotifications,
    setIsHistoryPage,
    setSelectedConversationId,
    setSelectedConversationLastDate,
    setIsTransferNotification,
    clearTransferNotification,    
    fetchPendingTransfers, 
  } = useSession()
  const [isOpen, setIsOpen] = useState(false)
  const [chatDialog, setChatDialog] = useState<{
    isOpen: boolean
    assignedBy: string
    assignerName: string
    transferId: number
  }>({
    isOpen: false,
    assignedBy: '',
    assignerName: '',
    transferId: 0
  })

  const myAssignedNotifications = useMemo(() => {
    if (!session?.user?.id || !notifications.transfers) return []

    const assigned: AssignedNotification[] = []

    Object.entries(notifications.transfers).forEach(([userId, transfers]) => {
      transfers.forEach((transfer: any) => {
        if (
          transfer.assigned_to === session.user.id &&
          transfer.assigned_by &&
          transfer.assigned_by.trim() !== "" &&
          !transfer.is_open
        ) {
          assigned.push({
            id: transfer.transferId,
            date: transfer.date || new Date().toISOString(),
            question: transfer.question || "",
            is_urgent: transfer.is_urgent || false,
            is_read: transfer.is_read || false,
            user_id: userId,
            assigned_by: transfer.assigned_by,
            assigned_to: transfer.assigned_to,
            collaborations: transfer.collaborations || [],
            is_open: transfer.is_open || false,
            is_important: transfer.is_important || false,
            is_ignored: transfer.is_ignored || false,
          })
        }
      })
    })

    return assigned.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    )
  }, [notifications.transfers, session?.user?.id])

  const unreadCount = myAssignedNotifications.length

  const isRecentAssignment = (dateString: string) => {
    const assignmentDate = new Date(dateString)
    const now = new Date()
    const diffHours =
      (now.getTime() - assignmentDate.getTime()) / (1000 * 60 * 60)
    return diffHours <= 24
  }

  const getAssignerName = (assignedById: string) => {
    const assigner = allAssistants.find(
      (assistant: any) => assistant.id === assignedById
    )
    return assigner?.name || t("unknown_user")
  }

  const getUserName = (userId: string) => {
    const user = myUsers.find((u: any) => String(u.id) === userId)
    return user?.displayName || t("unknown_patient")
  }

  const getUserPhone = (userId: string) => {
    const user = myUsers.find((u: any) => String(u.id) === userId)
    return user?.phone || null
  }

  const handleNotificationClick = async (notification: AssignedNotification) => {
    try {
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/communications/markTransferAsSeen/${notification.id}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `${session!.token_type} ${session!.access_token}`,
            "Content-Type": "application/json",
          },
        }
      )
      const userPhone = getUserPhone(notification.user_id)
      
      if (userPhone) {
        const activeNotificationData = {
          userId: Number(notification.user_id),
          important: notification.is_important,
          urgent: notification.is_urgent,
          read: notification.is_read,
          transferId: notification.id,
          collaborations: notification.collaborations,
          date: notification.date,
        }

        setActiveNotification(activeNotificationData)
        setActivePhone(userPhone)
        setViewingNotifications(true)
        setIsHistoryPage(false)
        setSelectedConversationId(null)
        setSelectedConversationLastDate(null)
        setIsTransferNotification(true)
      }

      setIsOpen(false)
      await fetchPendingTransfers();

    } catch (error) {
      console.error("Error handling notification click:", error)
    }
  }

  const handleChatClick = (e: React.MouseEvent, notification: AssignedNotification) => {
    e.stopPropagation()
    const assignerName = getAssignerName(notification.assigned_by)
    setChatDialog({
      isOpen: true,
      assignedBy: notification.assigned_by,
      assignerName,
      transferId: notification.id
    })
  }

  const closeChatDialog = () => {
    setChatDialog({
      isOpen: false,
      assignedBy: '',
      assignerName: '',
      transferId: 0
    })
  }

  const markAllAsSeen = async () => {
    for (const notification of myAssignedNotifications) {
      try {
        await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/communications/markTransferAsSeen/${notification.id}`,
          {
            method: "PATCH",
            headers: {
              Authorization: `${session!.token_type} ${session!.access_token}`,
              "Content-Type": "application/json",
            },
          }
        )
      } catch (error) {
        console.error("Error marking notification as seen:", error)
      }
    }

    setIsOpen(false)
    await fetchPendingTransfers();
  }

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="relative">
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <Badge
                variant="destructive"
                className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </Badge>
            )}
            <span className="sr-only">{t("notifications")}</span>
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-80 p-0">
          <div className="p-4 border-b">
            <h3 className="font-semibold text-sm">{t("assigned_notifications")}</h3>
            <p className="text-xs text-muted-foreground">
              {unreadCount > 0 ? (
                <>
                  {unreadCount} {t("unread_notifications_count")}
                </>
              ) : (
                t("no_new_notifications")
              )}
            </p>
          </div>

          <ScrollArea className="max-h-96">
            {myAssignedNotifications.length > 0 &&
              <div className="divide-y">
                {myAssignedNotifications.map((notification) => {
                  const isRecent = isRecentAssignment(notification.date)
                  const assignerName = getAssignerName(notification.assigned_by)
                  const userName = getUserName(notification.user_id)

                  return (
                    <div
                      key={notification.id}
                      className="p-4 hover:bg-muted/50 cursor-pointer transition-colors bg-blue-50 border-l-4 border-l-blue-500"
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="space-y-2">
                        <div className="flex flex-col items-start justify-between gap-2">
                          <div className="flex-1 w-full flex items-center justify-between">
                            <p className="text-sm font-medium">
                              {assignerName} {t("notification_assigned_by")}
                            </p>
                            <button
                              onClick={(e) => handleChatClick(e, notification)}
                              className="flex items-center gap-1 px-2 py-1 text-xs bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors"
                              title={t("chat_with_assigner")}
                            >
                              <MessageCircle className="h-3 w-3" />
                              {t("chat")}
                            </button>
                          </div>
                          <div className="flex w-full items-center justify-between gap-2 text-xs text-muted-foreground">
                            <span>
                              {t("patient")}: {userName}
                            </span>
                            <span>•</span>
                            <span>
                              {new Date(notification.date).toLocaleDateString()}
                            </span>
                            {notification.is_urgent && (
                              <>
                                <span>•</span>
                                <span className="text-red-600 font-medium">
                                  {t("urgent")}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex w-full text-sm text-muted-foreground">
                          <p className="line-clamp-2">
                            {notification.question || t("no_description_available")}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                          <span className="text-xs text-blue-600 font-medium">
                            {t("new_assignment")}
                          </span>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            }
          </ScrollArea>

          {myAssignedNotifications.length > 0 && (
            <div className="p-2 border-t">
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs"
                onClick={markAllAsSeen}
              >
                {t("mark_all_as_seen")}
              </Button>
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AssignmentChatDialog
        isOpen={chatDialog.isOpen}
        onClose={closeChatDialog}
        assignedBy={chatDialog.assignedBy}
        assignerName={chatDialog.assignerName}
        transferId={chatDialog.transferId}
      />
    </>
  )
}

export default NotificationBell
